/**
 * Module Icon Mappings
 *
 * Centralized icon definitions for all modules in the application.
 * Icons are consistent across sidebar navigation, page headers, and breadcrumbs.
 *
 * Uses a combination of:
 * - MUI Icons (@mui/icons-material)
 * - Lucide React (lucide-react)
 * - React Feather (react-feather) - for backward compatibility
 *
 * @see .github/copilot-instructions.md#components--ui
 */

import React from 'react';
// MUI Icons
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  IntegrationInstructions as IntegrationIcon,
  AccountTree as RealmIcon,
  Business as TenantIcon,
  Storage as SandboxIcon,
  Assessment as ReportsIcon,
  Person as ProfileIcon,
  BarChart as StatisticsIcon,
  CalendarMonth as DateIcon,
  FilterList as FiltersIcon,
  Public as GlobalIcon,
  RequestPage as RequestStatusIcon,
  Extension as RetoolIcon,
  Apps as ThirdPartyIcon,
  AdminPanelSettings as AdminIcon,
  Groups as TeamIcon,
  Work as CsmIcon,
  AccountBalance as BudgetIcon,
  ViewKanban as BoardIcon,
  Shield as ShieldIcon,
  Lock as AuthIcon,
  Layers as IsoStateIcon,
  CreditCard as SubscriptionIcon,
  Article as LogsIcon,
  ErrorOutline as ErrorIcon,
  SwapHoriz as MigrationIcon,
} from '@mui/icons-material';

// Lucide React Icons
import {
  Users,
  Briefcase,
  Trash2,
  DollarSign,
  Wrench,
  Shield,
  Grid,
  BarChart2,
} from 'lucide-react';

/**
 * Icon size configurations for different contexts
 */
export const ICON_SIZES = {
  sidebar: 20,
  pageHeader: 28,
  breadcrumb: 18,
  small: 16,
  medium: 22,
  large: 28,
} as const;

/**
 * Icon factory functions - creates icons with proper sizing
 * This ensures all icons are rendered at consistent sizes
 */
const createMuiIcon = (IconComponent: any, size = ICON_SIZES.sidebar) => (
  <IconComponent sx={{ fontSize: size, width: size, height: size }} />
);

const createLucideIcon = (IconComponent: any, size = ICON_SIZES.sidebar) => (
  <IconComponent size={size} strokeWidth={2} />
);

/**
 * Module icon registry
 * Maps module IDs to their respective icon components
 * All icons are now guaranteed to be 22x22px in the sidebar
 */
export const MODULE_ICONS: Record<string, React.ReactNode> = {
  // Main navigation modules
  Reports: createLucideIcon(BarChart2),
  Teams: createLucideIcon(Users), // Legacy - for backward compatibility
  'Teams Management': createLucideIcon(Briefcase),
  Members: createMuiIcon(PeopleIcon),
  'Teams Data Deletion Review': createLucideIcon(Trash2),
  'Pricing Calculator': createLucideIcon(DollarSign),

  // Administration modules
  Administration: createLucideIcon(Shield),
  'Super Admins': createMuiIcon(AdminIcon),
  Tenant: createMuiIcon(TenantIcon),
  Tenants: createMuiIcon(TenantIcon), // Plural form
  Subscription: createMuiIcon(SubscriptionIcon),
  Subscriptions: createMuiIcon(SubscriptionIcon), // Plural form
  Sandbox: createMuiIcon(SandboxIcon),
  Sandboxes: createMuiIcon(SandboxIcon), // Plural form
  'Tenant & Realm Management': createMuiIcon(RealmIcon),
  'Realm Migration': createMuiIcon(MigrationIcon),

  // Tools
  Tools: createLucideIcon(Wrench),
  'CSM Tools': createMuiIcon(CsmIcon),
  'Data Management': createMuiIcon(SettingsIcon),
  'Team Lifecycle': createMuiIcon(TeamIcon),

  // Integrations
  Integrations: createLucideIcon(Grid),
  Integration: createMuiIcon(IntegrationIcon), // Singular for pages

  // Logs
  Logs: createMuiIcon(LogsIcon),

  // Realm (singular, for pages)
  Realm: createMuiIcon(RealmIcon),

  // CSM (singular, for pages)
  CSM: createMuiIcon(CsmIcon),

  // Module-specific icons (for breadcrumbs and page headers)
  AuthModule: createMuiIcon(AuthIcon),
  BoardModule: createMuiIcon(BoardIcon),
  BudgetModule: createMuiIcon(BudgetIcon),
  CsmModule: createMuiIcon(CsmIcon),
  DateModule: createMuiIcon(DateIcon),
  FiltersModule: createMuiIcon(FiltersIcon),
  GlobalModule: createMuiIcon(GlobalIcon),
  IntegrationModule: createMuiIcon(IntegrationIcon),
  IsoStateModule: createMuiIcon(IsoStateIcon),
  ProfileModule: createMuiIcon(ProfileIcon),
  RealmModule: createMuiIcon(RealmIcon),
  ReportsModule: createMuiIcon(ReportsIcon),
  RequestStatusModule: createMuiIcon(RequestStatusIcon),
  RetoolModule: createMuiIcon(RetoolIcon),
  SandboxModule: createMuiIcon(SandboxIcon),
  StatisticsModule: createMuiIcon(StatisticsIcon),
  TeamModule: createMuiIcon(TeamIcon),
  TenantModule: createMuiIcon(TenantIcon),
  ThirdPartyModule: createMuiIcon(ThirdPartyIcon),

  // Auth pages
  Auth: createMuiIcon(ShieldIcon),
  'Sign In': createMuiIcon(AuthIcon),
  'Sign Up': createMuiIcon(AuthIcon),

  // Generic pages
  Pages: createMuiIcon(DashboardIcon),
  Profile: createMuiIcon(ProfileIcon),
  'Pending Entity': createMuiIcon(RequestStatusIcon),
  'Team-specific Integration': createMuiIcon(IntegrationIcon),
  'Integration Health': createMuiIcon(IntegrationIcon),
  'Entity Errors': createMuiIcon(ErrorIcon),
} as const;

/**
 * Get icon for a module or route
 * Returns the icon component with optional size override
 */
export const getModuleIcon = (
  moduleId: string,
  size?: keyof typeof ICON_SIZES,
): React.ReactNode => {
  const icon = MODULE_ICONS[moduleId];

  if (!icon) {
    // Default fallback icon
    return <DashboardIcon sx={{ fontSize: size ? ICON_SIZES[size] : ICON_SIZES.sidebar }} />;
  }

  // If size override provided, clone the icon with new size
  if (size && React.isValidElement(icon)) {
    const iconSize = ICON_SIZES[size];

    // Handle MUI icons (have sx prop)
    if ('sx' in icon.props) {
      return React.cloneElement(icon, {
        sx: {
          fontSize: iconSize,
          width: iconSize,
          height: iconSize,
        },
      } as any);
    }

    // Handle Lucide/Feather icons (have size prop)
    if ('size' in icon.props) {
      return React.cloneElement(icon, {
        size: iconSize,
      } as any);
    }
  }

  return icon;
};

/**
 * Route path to module mapping
 * Maps URL patterns to module IDs for breadcrumb generation
 */
export const ROUTE_TO_MODULE_MAP: Record<string, string> = {
  '/reports': 'Reports',
  '/teams-2': 'Teams Management',
  '/members': 'Members',
  '/data-deletion-review': 'Teams Data Deletion Review',
  '/pricing-calculator': 'Pricing Calculator',
  '/admin': 'Administration',
  '/admin/super-admins': 'Super Admins',
  '/admin/tenant': 'Tenants',
  '/admin/subscription': 'Subscriptions',
  '/admin/sandbox': 'Sandboxes',
  '/admin/tenant-realm': 'Tenant & Realm Management',
  '/realm-migration': 'Realm Migration',
  '/tools': 'Tools',
  '/tools/csm': 'CSM Tools',
  '/tools/data': 'Data Management',
  '/tools/team-lifecycle': 'Team Lifecycle',
  '/integrations': 'Integrations',
  '/integration/overall': 'Integration',
  '/integration/details': 'Integration',
  '/integration/health': 'Integration',
  '/integration/logs': 'Logs',
  '/integration/pendingEntity': 'Pending Entity',
  '/integration/entityErrors': 'Entity Errors',
  '/profile': 'Profile',
  '/auth': 'Auth',
} as const;

/**
 * Get module name from route path
 */
export const getModuleNameFromPath = (pathname: string): string | null => {
  // Try exact match first
  if (ROUTE_TO_MODULE_MAP[pathname]) {
    return ROUTE_TO_MODULE_MAP[pathname];
  }

  // Try partial match (for nested routes)
  const matchingKey = Object.keys(ROUTE_TO_MODULE_MAP).find((key) => pathname.startsWith(key));

  return matchingKey ? ROUTE_TO_MODULE_MAP[matchingKey] : null;
};
